import { useEffect, useState } from "react";
import { MapPin, School, Truck, Clock, Ban } from "lucide-react";
import Section, { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import PincodeChecker from "@/components/PincodeChecker";
import CTAButton from "@/components/CTAButton";
import api from "@/lib/api";

export default function DeliveryAreasPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/serviceability/areas")
      .then((r) => setData(r.data))
      .catch(() => setData({ city: "Pune", areas: [], schools: [] }));
  }, []);

  const active = data?.areas.filter((a) => a.status === "active") || [];
  const waitlist = data?.areas.filter((a) => a.status !== "active") || [];

  return (
    <>
      <PageHero
        eyebrow="Delivery and service areas"
        title="Delivering across Pune, zone by zone."
        intro="We would rather deliver reliably to a few areas than unreliably to the whole city. Check your pincode below to see where you stand."
      >
        <div className="max-w-xl">
          <PincodeChecker compact />
        </div>
      </PageHero>

      <Section id="zones" eyebrow="Live zones" title="Where we deliver today">
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="active-areas">
          {active.map((a, i) => (
            <Reveal key={a.pincode} delay={i * 60}>
              <div className="mbm-card h-full p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-heading text-base font-semibold">{a.area}</h3>
                  <span className="rounded-full bg-sageSoft px-2.5 py-1 font-heading text-[11px] font-semibold text-coralInk">
                    {a.pincode}
                  </span>
                </div>
                <p className="mt-1 text-[12px] font-medium uppercase tracking-[0.1em] text-cocoa/70">
                  {a.zone}
                </p>
                <ul className="mt-4 space-y-2 text-[13px] text-cocoa/70">
                  <li className="flex gap-2">
                    <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coralInk" aria-hidden="true" />
                    {a.delivery_days}
                  </li>
                  <li className="flex gap-2">
                    <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coralInk" aria-hidden="true" />
                    {a.delivery_window} · cut-off {a.cutoff}
                  </li>
                  <li className="flex gap-2">
                    <School className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coralInk" aria-hidden="true" />
                    {a.schools.join(", ")}
                  </li>
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section
        id="waitlist"
        tone="creamDeep"
        eyebrow="Next in line"
        title="Areas on the waitlist"
        intro="These areas are mapped but not served yet. Requesting a plan from one of them moves it up the list."
      >
        <ul className="mt-8 flex flex-wrap gap-3" data-testid="waitlist-areas">
          {waitlist.map((a) => (
            <li
              key={a.pincode}
              className="flex items-center gap-2 rounded-control bg-surface shadow-soft px-4 py-3 text-sm font-medium text-cocoa"
            >
              <MapPin className="h-4 w-4 text-rust" aria-hidden="true" />
              {a.area} · {a.pincode}
            </li>
          ))}
        </ul>
        <div className="mt-10">
          <CTAButton to="/get-started" data-testid="delivery-early-access-cta">
            Add my area to the list
          </CTAButton>
        </div>
      </Section>

      <Section id="delivery-rules" tone="sage" eyebrow="Good to know" title="How delivery works">
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Truck, title: "Delivery days", copy: "Monday to Friday, excluding declared holidays." },
            { icon: Clock, title: "Cut-off times", copy: "Skips, pauses and changes must reach us before 8:00 pm the previous day." },
            { icon: School, title: "School delivery", copy: "Available where the school permits it and our route covers it." },
            { icon: Ban, title: "Temporarily unavailable", copy: "If a zone pauses for capacity or weather, subscribers are told the same day." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 70}>
              <div className="mbm-card h-full p-6">
                <c.icon className="h-5 w-5 text-coralInk" aria-hidden="true" />
                <h3 className="mt-4 font-heading text-base font-semibold">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-cocoa/70">{c.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>
    </>
  );
}
